import { ReactElement } from 'react';
import { Link, useRoutes } from 'react-router-dom';
import { CreateScene, ModelScene, IndexScene, useEntity, EntityScene, UpdateScene, DeleteScene } from 'components';
import { formatDate } from 'utilities';
import { ControlledTableColumns } from 'components/ControlledTable';
import type { Vendor } from 'modules/supplier/vendor/types';
import { PriceList } from 'modules/supplier/pricelist/types';
import { PriceListForm } from 'modules/supplier/pricelist/PriceListForm';
import { Nav } from './Nav';
import { Factors } from './Factors';
import { Prices } from './Prices';
import { parseISO, addDays, formatISO } from 'date-fns';

const columns: ControlledTableColumns<PriceList> = [
  {
    content: 'Period',
    key: 'date_from',
    required: true,
    sortable: true,
    width: 220,
    render: ({ id, date_from, date_to }: PriceList) => (
      <Link to={`${id}`}>
        {formatDate(date_from)} - {formatDate(date_to)}
      </Link>
    ),
  },
  {
    content: 'Type',
    key: 'type',
  },
];

const initialValues = {
  date_from: null,
  date_to: null,
  type: undefined,
};

const copy = ({ date_to, type }: PriceList) => ({
  ...initialValues,
  date_from: date_to ? formatISO(addDays(parseISO(date_to), 1), { representation: 'date' }) : null,
  type,
});

export function VendorPriceLists(): ReactElement {
  const {
    endpoint,
    item: { id },
  } = useEntity<Vendor>();

  const model = {
    endpoint: `${endpoint}price_lists/`,
    title: 'Price list',
    pluralTitle: 'Price lists',
    entityTitle: ({ date_from, date_to, type }: PriceList) =>
      `${formatDate(date_from)} - ${formatDate(date_to)} (${type})`,
  };

  const routes = useRoutes([
    {
      index: true,
      element: <IndexScene columns={columns} />,
    },
    {
      path: 'create',
      element: (
        <CreateScene initialValues={initialValues}>
          <PriceListForm create />
        </CreateScene>
      ),
    },
    {
      path: ':id/',
      element: (
        <EntityScene>
          <Nav />
        </EntityScene>
      ),
      children: [
        {
          index: true,
          element: (
            <UpdateScene viewDisabled={true} heading={null} copy={copy}>
              <PriceListForm create={false} />
            </UpdateScene>
          ),
        },
        {
          path: 'prices/*',
          element: <Prices vendorId={id} />,
        },
        {
          path: 'factors/*',
          element: <Factors />,
        },
        {
          path: 'delete',
          element: <DeleteScene />,
        },
      ],
    },
  ]);
  return <ModelScene {...model}>{routes}</ModelScene>;
}
